import React, { useState, useEffect } from 'react';
import { ShoppingBag, Star, Heart, Truck, Shield, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../AuthContext';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';

const Shop = () => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({ items: [], totals: { total: 0 } });
  
  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get(`/shop/products?category=${selectedCategory}`);
        setProducts(response.data.products || []);
        setCategories(response.data.categories || []);
      } catch (error) {
        console.error('Error fetching products:', error);
        toast({
          title: "Error",
          description: "Failed to load products",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, [selectedCategory, toast]);
  
  // Fetch cart data if user is authenticated
  useEffect(() => {
    const fetchCart = async () => {
      if (!token) return;
      
      try {
        const response = await api.get('/shop/cart', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCart(response.data);
      } catch (error) {
        console.error('Error fetching cart:', error);
      }
    };
    
    fetchCart();
  }, [token]);
  
  const addToCart = async (product, variant) => {
    if (!token) {
      toast({
        title: "Login Required",
        description: "Please log in to add items to cart",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const response = await api.post('/shop/cart/add', {
        product_id: product.id,
        variant: variant,
        quantity: 1
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCart(response.data.cart);
      toast({
        title: "✅ Added to Cart!",
        description: `${product.name} added to your cart`,
        duration: 3000,
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast({
        title: "Error",
        description: "Failed to add item to cart",
        variant: "destructive",
      });
    }
  };

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(product => product.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <ShoppingBag className="w-8 h-8 text-rose-500 mr-3" />
            <h1 className="text-4xl font-bold text-gray-800">Constant Love Co. Shop</h1>
          </div>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Spread self-love with our curated collection of affirmation apparel and accessories
          </p>
          
          {/* Trust Indicators */}
          <div className="flex items-center justify-center space-x-8 mt-6 text-sm text-gray-500">
            <div className="flex items-center space-x-2">
              <Truck className="w-4 h-4" />
              <span>Free shipping over $50</span>
            </div>
            <div className="flex items-center space-x-2">
              <Shield className="w-4 h-4" />
              <span>30-day returns</span>
            </div>
            <div className="flex items-center space-x-2">
              <Heart className="w-4 h-4" />
              <span>Made with love</span>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {categories.map((category) => (
            <Button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              variant={selectedCategory === category.id ? "default" : "outline"}
              className={`rounded-full ${
                selectedCategory === category.id
                  ? 'bg-rose-500 hover:bg-rose-600 text-white'
                  : 'border-rose-300 text-rose-600 hover:bg-rose-50'
              }`}
            >
              {category.name}
            </Button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
          {loading ? (
            // Loading skeleton
            Array.from({ length: 8 }).map((_, index) => (
              <Card key={index} className="overflow-hidden">
                <div className="animate-pulse">
                  <div className="w-full h-48 bg-gray-200"></div>
                  <div className="p-4">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded mb-3"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2 mb-3"></div>
                    <div className="flex justify-between items-center">
                      <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                      <div className="h-8 bg-gray-200 rounded w-20"></div>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            filteredProducts.map((product) => (
              <Card key={product.id} className="group cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden">
                <div className="relative">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {product.badge && (
                    <span className={`absolute top-3 left-3 px-2 py-1 text-xs font-medium rounded-full ${
                      product.badge === 'Bestseller' ? 'bg-amber-100 text-amber-800' :
                      product.badge === 'Premium' ? 'bg-purple-100 text-purple-800' :
                      product.badge === 'New' ? 'bg-green-100 text-green-800' :
                      product.badge === 'Digital' ? 'bg-blue-100 text-blue-800' : ''
                    }`}>
                      {product.badge}
                    </span>
                  )}
                  <Heart className="absolute top-3 right-3 w-5 h-5 text-white hover:text-rose-500 hover:fill-rose-500 transition-colors cursor-pointer" />
                </div>
                
                <div className="p-4">
                  <h3 className="font-semibold text-gray-800 mb-2 group-hover:text-rose-600 transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">{product.description}</p>
                  
                  <div className="flex items-center mb-3">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`w-3 h-3 ${
                            i < Math.floor(product.rating) 
                              ? 'text-yellow-400 fill-yellow-400' 
                              : 'text-gray-300'
                          }`} 
                        />
                      ))}
                    </div>
                    <span className="text-xs text-gray-500 ml-2">({product.reviews})</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg font-bold text-gray-800">{product.price}</span>
                      {product.originalPrice && (
                        <span className="text-sm text-gray-500 line-through">{product.originalPrice}</span>
                      )}
                    </div>
                    <Button 
                      size="sm"
                      className="bg-rose-500 hover:bg-rose-600 text-white rounded-full px-4"
                      onClick={() => addToCart(product, 'default')}
                    >
                      Add to Cart
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* CTA Section */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 text-center border border-rose-200">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Join the Constant Love Community
          </h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Get early access to new drops, exclusive discounts, and join thousands spreading self-love worldwide
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <input 
              type="email" 
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-full border border-rose-200 focus:outline-none focus:ring-2 focus:ring-rose-300"
            />
            <Button className="bg-rose-500 hover:bg-rose-600 text-white rounded-full px-8">
              Join Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;